import { TellescopeModelFromName, useUsersQuery } from "@tellescope/tanstack-query";
import { useCallback, useState } from "react";
import { getUnavailableUsers } from "../utils";

export interface UnavailableUsersGuardRequest {
  userIds: string[];
  start: Date;
  durationInMinutes: number;
}

interface PendingConfirmation {
  unavailableUsers: TellescopeModelFromName<"users">[];
  onConfirm: () => void;
}

/**
 * Guards an action (event create or update) behind a warning when one or more of
 * the provided users is unavailable during the target time range.
 *
 * Call `guard(request, onConfirm)`: if every user is available (or none are
 * provided), `onConfirm` runs immediately. Otherwise the warning dialog opens and
 * `onConfirm` only runs if the user chooses "Continue anyway". Spread
 * `warningDialogProps` onto `UnavailableUsersWarningDialog`.
 */
export const useUnavailableUsersGuard = ({
  timezone = Intl.DateTimeFormat().resolvedOptions().timeZone,
}: {
  timezone?: string;
}) => {
  const { data: users = [] } = useUsersQuery();
  const [pending, setPending] = useState<PendingConfirmation | null>(null);

  const guard = useCallback(
    (request: UnavailableUsersGuardRequest, onConfirm: () => void) => {
      const unavailableUsers = getUnavailableUsers(
        users,
        request.userIds,
        request.start,
        request.durationInMinutes,
        timezone
      );
      if (unavailableUsers.length === 0) {
        onConfirm();
        return;
      }
      setPending({ unavailableUsers, onConfirm });
    },
    [users, timezone]
  );

  const handleOpenChange = useCallback((open: boolean) => {
    if (!open) setPending(null);
  }, []);

  const handleConfirm = useCallback(() => {
    pending?.onConfirm();
  }, [pending]);

  return {
    guard,
    warningDialogProps: {
      open: !!pending,
      onOpenChange: handleOpenChange,
      onConfirm: handleConfirm,
      unavailableUsers: pending?.unavailableUsers ?? [],
    },
  };
};
